/**
 * ===========================================
 * StudySpace ERP Frontend - Offline Sync Hook
 * ===========================================
 * 
 * Custom hook for managing offline data synchronization.
 * Automatically syncs queued payments when network reconnects.
 * 
 * Features:
 * - Network status monitoring
 * - Automatic sync on reconnect
 * - Manual sync trigger
 * - Queue status tracking
 * 
 * @file src/hooks/useOfflineSync.js
 * @description Offline synchronization hook
 * @task T199
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getQueue,
  getPendingItems,
  updateQueueItem,
  removeFromQueue,
  getQueueStats,
  hasPendingItems,
  MAX_RETRY_ATTEMPTS
} from '../utils/offlineQueue';
import { createPayment } from '../api/payments.api';
import { createSale, createCafePayment } from '../api/cafe.api';
import { checkOut } from '../api/sessions.api';

/**
 * useOfflineSync Hook
 * 
 * Manages offline data synchronization and network status.
 * 
 * @returns {Object} Sync state and control functions
 * 
 * @example
 * const { isOnline, isSyncing, pendingCount, triggerSync } = useOfflineSync();
 */
const useOfflineSync = () => {
  // State for network status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // State for sync status
  const [isSyncing, setIsSyncing] = useState(false);

  // State for queue statistics
  const [queueStats, setQueueStats] = useState(getQueueStats());

  // Ref to prevent concurrent sync operations
  const syncInProgress = useRef(false);

  /**
   * Update queue stats from localStorage
   */
  const refreshQueueStats = useCallback(() => {
    setQueueStats(getQueueStats());
  }, []);

  /**
   * Process a single queued item
   * 
   * @param {Object} item - Queue item to process
   * @returns {Promise<boolean>} Success status
   */
  const processQueueItem = useCallback(async (item) => {
    try {
      // Mark as processing
      updateQueueItem(item.id, { status: 'processing', attempts: item.attempts + 1 });

      let result;

      // Process based on type
      switch (item.type) {
        case 'session_payment':
          result = await createPayment({
            sessionId: item.data.sessionId,
            workspace: item.data.workspaceId,
            amount: item.data.amount,
            notes: item.data.notes || ''
          });
          break;

        case 'session_checkout_payment':
          try {
            await checkOut(item.data.sessionId, {
              notes: item.data.notes || ''
            });
          } catch (checkoutError) {
            // If session is already checked out, proceed to payment
            const message = checkoutError?.message || '';
            if (!message.toLowerCase().includes('already') && !message.toLowerCase().includes('completed')) {
              throw checkoutError;
            }
          }

          result = await createPayment({
            sessionId: item.data.sessionId,
            workspace: item.data.workspaceId,
            amount: item.data.amount,
            notes: item.data.notes || ''
          });
          break;

        case 'cafe_payment': {
          const saleResponse = await createSale({
            items: item.data.items,
            workspace: item.data.workspaceId,
            customerId: item.data.customerId || undefined,
            notes: item.data.notes || ''
          });

          const sale = saleResponse?.payload?.sale;
          if (!sale?._id) {
            throw new Error('Failed to create cafe sale during sync');
          }

          result = await createCafePayment({
            cafeSaleId: sale._id,
            workspace: item.data.workspaceId,
            notes: item.data.notes || ''
          });
          break;
        }

        default:
          throw new Error(`Unknown queue item type: ${item.type}`);
      }

      // Mark as completed and remove from queue
      updateQueueItem(item.id, { status: 'completed', result });
      removeFromQueue(item.id);

      return true;
    } catch (error) {
      console.error(`Failed to sync queue item ${item.id}:`, error);

      // Check if max retries exceeded
      if (item.attempts + 1 >= MAX_RETRY_ATTEMPTS) {
        updateQueueItem(item.id, {
          status: 'failed',
          error: error.message,
          lastAttempt: new Date().toISOString()
        });
      } else {
        // Reset to pending for retry
        updateQueueItem(item.id, {
          status: 'pending',
          lastError: error.message,
          lastAttempt: new Date().toISOString()
        });
      }

      return false;
    }
  }, []);

  /**
   * Sync all pending items in the queue
   * 
   * @returns {Promise<Object>} Sync results
   */
  const syncQueue = useCallback(async () => {
    // Prevent concurrent sync operations
    if (syncInProgress.current || !navigator.onLine) {
      return { success: false, reason: syncInProgress.current ? 'sync_in_progress' : 'offline' };
    }

    const pendingItems = getPendingItems();

    if (pendingItems.length === 0) {
      return { success: true, synced: 0 };
    }

    syncInProgress.current = true;
    setIsSyncing(true);

    let successCount = 0;
    let failCount = 0;

    try {
      // Process items sequentially to maintain order
      for (const item of pendingItems) {
        // Check if still online
        if (!navigator.onLine) {
          break;
        }

        const success = await processQueueItem(item);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }

        // Update stats after each item
        refreshQueueStats();
      }

      // Show toast with results
      if (successCount > 0) {
        toast.success(`Synced ${successCount} offline payment${successCount > 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to sync ${failCount} payment${failCount > 1 ? 's' : ''}`);
      }

      return {
        success: failCount === 0,
        synced: successCount,
        failed: failCount
      };
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
      refreshQueueStats();
    }
  }, [processQueueItem, refreshQueueStats]);

  /**
   * Manually trigger sync
   */
  const triggerSync = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error('Cannot sync while offline');
      return { success: false, reason: 'offline' };
    }

    return syncQueue();
  }, [syncQueue]);

  // Network status listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online');

      // Auto-sync when coming back online
      if (hasPendingItems()) {
        setTimeout(() => {
          syncQueue();
        }, 1000); // Small delay to ensure stable connection
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error('You are offline. Payments will be queued.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncQueue]);

  // Queue update listener
  useEffect(() => {
    const handleQueueUpdate = () => {
      refreshQueueStats();
    };

    window.addEventListener('offlineQueueUpdated', handleQueueUpdate);

    return () => {
      window.removeEventListener('offlineQueueUpdated', handleQueueUpdate);
    };
  }, [refreshQueueStats]);

  // Initial sync attempt when hook mounts
  useEffect(() => {
    if (isOnline && hasPendingItems()) {
      syncQueue();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // Network status
    isOnline,

    // Sync status
    isSyncing,

    // Queue statistics
    queueStats,
    pendingCount: queueStats.pending,
    failedCount: queueStats.failed,

    // Check for pending items
    hasPendingItems: queueStats.pending > 0,
    hasFailedItems: queueStats.failed > 0,

    // Actions
    triggerSync,
    refreshQueueStats,

    // Raw queue access
    getQueue,
    getPendingItems
  };
};

export default useOfflineSync;
